/**
 * Spot Request Types for the Spot Change Request Management feature
 */

export interface MergeSpotRequest {
  id: number;
  userid: number;
  targetspotid: number;
  selectedspotid?: number;  // Source spot ID
  sourcespotid?: number;    // Alternative name for source spot ID
  username: string;
  targetspotname: string;
  sourcespotname: string;
  // Coordinates - may be null or undefined
  targetLat?: number | null;
  targetLon?: number | null;
  sourceLat?: number | null;
  sourceLon?: number | null;
  // Reviews and sessions - using API field names
  targetnreviews?: number;
  sourcenreviews?: number;
  targetnsessions?: number;
  sourcensessions?: number;
  createdAt?: string;
  comment?: string;
}

export interface SpotAddRequest {
  id: number;
  type: 'ADD' | 'RENAME' | 'EDIT';
  spotname: string;
  oldname?: string;
  newname?: string;
  latitude: number;
  longitude: number;
  oldlatitude?: number | null;
  oldlongitude?: number | null;
  spotidtorename?: number | null;
  userid: number;
  username?: string;
  createdAt?: string;
  countryName?: string;
  alpha2?: string;
  comment?: string;
}

export type SpotRequestType = 'merge' | 'add' | 'rename' | 'edit';

export interface SpotRequest {
  type: SpotRequestType;
  id: number;
  data: MergeSpotRequest | SpotAddRequest;
}

export interface SpotRequestFilters {
  requestType: 'all' | 'merge' | 'add' | 'rename' | 'edit';
  sortBy: 'newest' | 'oldest' | 'distance' | 'sessions';
  searchQuery: string;
}

export type ViewMode = 'riders' | 'requests';

// Helper function to calculate distance between two coordinates using Haversine formula
export const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 6371; // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

// Helper to get distance for merge request
export const getMergeRequestDistance = (request: MergeSpotRequest): number => {
  if (!isValidCoordinate(request.sourceLat) || !isValidCoordinate(request.sourceLon) ||
      !isValidCoordinate(request.targetLat) || !isValidCoordinate(request.targetLon)) {
    return 0;
  }
  return calculateDistance(
    request.sourceLat!,
    request.sourceLon!,
    request.targetLat!,
    request.targetLon!
  );
};

// Helper to check if a merge request has valid coordinates
export const hasMergeRequestValidCoordinates = (request: MergeSpotRequest): boolean => {
  return isValidCoordinate(request.sourceLat) &&
         isValidCoordinate(request.sourceLon) &&
         isValidCoordinate(request.targetLat) &&
         isValidCoordinate(request.targetLon);
};

// Helper to check if a coordinate is valid
export const isValidCoordinate = (value: number | null | undefined): value is number => {
  return typeof value === 'number' && !isNaN(value) && isFinite(value) && Math.abs(value) > 0.0001;
};
